import { useState, useMemo } from "react";
import {
  AlertCircle,
  AlertTriangle,
  CheckCircle,
  Mail,
  MessageSquare,
  Paperclip,
  Phone,
  Send,
  X,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api-client";
import { safeFormatDate } from "../lib/dateFormat";
import { useLocation } from "../contexts/LocationContext";
import {
  getOrderedCountries,
  defaultCountryCodeForTimezone,
} from "@reservamanager/shared-types";
import {
  getEmailStatusConfig,
  type CustomerEmailStatus,
} from "../lib/customerEmailStatus";

/**
 * Formulario de alta/edición de la ficha de cliente (con envío de email
 * incorporado). Vivía dentro de `pages/Customers.tsx`; se extrajo TAL CUAL
 * (ago 2026) para poder abrirlo también desde el detalle de una reserva
 * ("Editar ficha") sin duplicar el formulario. `Customer` y `EmailStatusBadge`
 * viajan con él porque los usa; la página de Clientes los importa de aquí.
 */

export interface Customer {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  countryCode: string;
  language: string;
  notes: string | null;
  tags: string[];
  vipStatus: boolean;
  marketingConsent: boolean;
  acceptsWhatsApp: boolean;
  transactionalEmailOptOut?: boolean;
  transactionalWhatsAppOptOut?: boolean;
  optOutAt?: string | null;
  optOutReason?: string | null;
  // Estado de comunicaciones comerciales por email (jul 2026).
  marketingEmailOptOut?: boolean;
  marketingOptOutAt?: string | null;
  emailStatus?: CustomerEmailStatus;
  blocked?: boolean;
  noShowCount?: number;
  primarySource?: string | null;
  // Nombres con los que ha reservado (widget/app) y que NO cuadran con esta
  // ficha ("a nombre de …"). Lo calcula el backend; vacío = todo en orden.
  bookingNameMismatches?: string[];
  // Gasto total según el TPV. Null = todavía ninguna cuenta.
  spend?: { totalCents: number; visits: number; currency: string } | null;
  createdAt: string;
  updatedAt: string;
  _count?: {
    reservations: number;
  };
}

/** Badge discreto de estado de comunicaciones por email, para filas de la lista. */
export function EmailStatusBadge({ status }: { status?: CustomerEmailStatus }) {
  const cfg = getEmailStatusConfig(status);
  // Estado desconocido (p. ej. backend antiguo durante un despliegue): sin
  // badge, mejor que etiquetar "Alta" a quien puede estar de baja.
  if (!cfg) return null;
  const Icon = cfg.icon;
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${cfg.badgeClassName}`}
    >
      <Icon className="w-3 h-3" />
      {cfg.label}
    </span>
  );
}


interface CustomerModalProps {
  customer: Customer | null;
  onClose: () => void;
  onSave: () => void;
}

export default function CustomerEditModal({ customer, onClose, onSave }: CustomerModalProps) {
  const { currentLocation } = useLocation();
  const navigate = useNavigate();

  // "Iniciar conversación por WhatsApp" desde la ficha (12-sep-2026): envía
  // la plantilla de apertura desde el número de la sede y abre el inbox.
  const [startingWhatsAppChat, setStartingWhatsAppChat] = useState(false);
  const startWhatsAppConversation = async () => {
    if (!customer || startingWhatsAppChat) return;
    setStartingWhatsAppChat(true);
    try {
      const res = await api.post("/whatsapp-conversations/start", {
        locationId: currentLocation?.id,
        customerId: customer.id,
      });
      const conversationId = res.data?.data?.conversationId as string | undefined;
      navigate(conversationId ? `/conversations?open=${conversationId}` : "/conversations");
    } catch (err) {
      const e = err as { response?: { data?: { message?: string } } };
      setError(e.response?.data?.message || "No se pudo iniciar la conversación por WhatsApp");
    } finally {
      setStartingWhatsAppChat(false);
    }
  };
  // Prefijo por defecto según la timezone de la sede activa (ej: "+52" para
  // una sede en México), solo se usa si el cliente no tiene ya uno guardado.
  const defaultCountryCode = defaultCountryCodeForTimezone(
    currentLocation?.timezone,
  );
  const orderedCountries = useMemo(
    () => getOrderedCountries(defaultCountryCode),
    [defaultCountryCode],
  );

  const [formData, setFormData] = useState({
    firstName: customer?.firstName || "",
    lastName: customer?.lastName || "",
    email: customer?.email || "",
    phone: customer?.phone || "",
    countryCode: customer?.countryCode || defaultCountryCode,
    notes: customer?.notes || "",
    vipStatus: customer?.vipStatus || false,
    marketingConsent: customer?.marketingConsent || false,
    acceptsWhatsApp: customer?.acceptsWhatsApp || false,
    transactionalEmailOptOut: customer?.transactionalEmailOptOut || false,
    transactionalWhatsAppOptOut: customer?.transactionalWhatsAppOptOut || false,
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Email modal state
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [emailFormData, setEmailFormData] = useState({
    subject: "",
    message: "",
  });
  const [emailAttachments, setEmailAttachments] = useState<File[]>([]);
  const [emailSending, setEmailSending] = useState(false);
  const [emailSuccess, setEmailSuccess] = useState<string | null>(null);
  const [emailError, setEmailError] = useState<string | null>(null);

  const handleSubmit = async () => {
    if (!formData.firstName.trim() || !formData.phone.trim()) {
      setError("Nombre y teléfono son obligatorios");
      return;
    }

    setSaving(true);
    setError(null);

    try {
      if (customer) {
        await api.put(`/customers/${customer.id}`, formData);
      } else {
        await api.post("/customers", formData);
      }
      onSave();
    } catch (err: unknown) {
      const axiosError = err as { response?: { data?: { message?: string } } };
      setError(axiosError.response?.data?.message || "Error al guardar");
    } finally {
      setSaving(false);
    }
  };

  // Función para abrir el teléfono
  const handleCall = () => {
    if (customer?.phone) {
      const phoneNumber = `${customer.countryCode || "+34"}${customer.phone.replace(/\s/g, "")}`;
      window.location.href = `tel:${phoneNumber}`;
    }
  };

  // Función para abrir el modal de email
  const handleOpenEmailModal = () => {
    if (customer?.email) {
      setEmailFormData({
        subject: "",
        message: `Estimado/a ${customer.firstName},\n\n`,
      });
      setEmailAttachments([]);
      setEmailError(null);
      setEmailSuccess(null);
      setShowEmailModal(true);
    }
  };

  // Función para enviar email
  const handleSendEmail = async () => {
    if (
      !customer?.id ||
      !emailFormData.subject.trim() ||
      !emailFormData.message.trim()
    ) {
      setEmailError("Asunto y mensaje son obligatorios");
      return;
    }

    setEmailSending(true);
    setEmailError(null);

    try {
      // Convert files to base64 for the API
      const attachmentsData = await Promise.all(
        emailAttachments.map(async (file) => {
          const base64 = await new Promise<string>((resolve) => {
            const reader = new FileReader();
            reader.onloadend = () => {
              const result = reader.result as string;
              const base64Data = result.split(",")[1];
              resolve(base64Data);
            };
            reader.readAsDataURL(file);
          });
          return {
            filename: file.name,
            content: base64,
            contentType: file.type,
          };
        }),
      );

      await api.post(`/customers/${customer.id}/send-email`, {
        subject: emailFormData.subject,
        message: emailFormData.message,
        attachments: attachmentsData,
      });

      setEmailSuccess("Email enviado correctamente");
      setTimeout(() => {
        setShowEmailModal(false);
        setEmailFormData({ subject: "", message: "" });
        setEmailAttachments([]);
        setEmailSuccess(null);
      }, 1500);
    } catch (err: unknown) {
      console.error("Error sending email:", err);
      const apiError = err as { response?: { data?: { error?: string } } };
      setEmailError(
        apiError.response?.data?.error || "Error al enviar el email",
      );
    } finally {
      setEmailSending(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      {/* Altura acotada + columna flex: cabecera y pie (Guardar) siempre a la
          vista y el formulario hace scroll vertical. Sin esto, centrado en un
          overlay flex, un formulario más alto que la ventana se cortaba por
          arriba y por abajo y el botón Guardar quedaba inaccesible. */}
      <div className="bg-white rounded-2xl w-full max-w-lg shadow-xl max-h-[90dvh] flex flex-col overflow-hidden">
        <div className="p-5 border-b border-gray-200 flex-shrink-0">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold text-gray-900">
              {customer ? "Editar Cliente" : "Nuevo Cliente"}
            </h2>
            {customer && (
              <div className="flex items-center gap-2">
                {customer.phone && (
                  <button
                    onClick={handleCall}
                    className="p-2 bg-green-100 text-green-700 rounded-lg hover:bg-green-200 transition-colors"
                    title={`Llamar a ${customer.countryCode || "+34"} ${customer.phone}`}
                  >
                    <Phone className="w-5 h-5" />
                  </button>
                )}
                {customer.phone && currentLocation?.whatsappPhoneNumberId && (
                  <button
                    onClick={startWhatsAppConversation}
                    disabled={startingWhatsAppChat}
                    className="p-2 bg-emerald-100 text-emerald-700 rounded-lg hover:bg-emerald-200 transition-colors disabled:opacity-50"
                    title="Iniciar conversación por WhatsApp desde el número del negocio"
                  >
                    <MessageSquare className="w-5 h-5" />
                  </button>
                )}
                {customer.email && (
                  <button
                    onClick={handleOpenEmailModal}
                    className="p-2 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition-colors"
                    title={`Enviar email a ${customer.email}`}
                  >
                    <Mail className="w-5 h-5" />
                  </button>
                )}
              </div>
            )}
          </div>
        </div>

        <div className="p-5 space-y-4 flex-1 overflow-y-auto">
          {error && (
            <div className="p-3 bg-red-50 text-red-700 text-sm rounded-lg">
              {error}
            </div>
          )}

          {/* Ha reservado con otro nombre: se avisa junto a los campos del
              nombre para que el staff corrija la ficha si procede. */}
          {customer?.bookingNameMismatches &&
            customer.bookingNameMismatches.length > 0 && (
              <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                <AlertTriangle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
                <p className="text-xs text-amber-800">
                  Este cliente ha reservado como{" "}
                  {customer.bookingNameMismatches.map((n, i) => (
                    <span key={n}>
                      {i > 0 && ", "}
                      <strong>«{n}»</strong>
                    </span>
                  ))}
                  . Puede ser una reserva a nombre de un grupo o empresa, o un
                  error en la ficha: si el nombre de abajo no es el suyo,
                  corrígelo.
                </p>
              </div>
            )}

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Nombre *
              </label>
              <input
                type="text"
                value={formData.firstName}
                onChange={(e) =>
                  setFormData({ ...formData, firstName: e.target.value })
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                placeholder="Nombre"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Apellidos
              </label>
              <input
                type="text"
                value={formData.lastName}
                onChange={(e) =>
                  setFormData({ ...formData, lastName: e.target.value })
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                placeholder="Apellidos"
              />
            </div>
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Prefijo
              </label>
              <select
                value={formData.countryCode}
                onChange={(e) =>
                  setFormData({ ...formData, countryCode: e.target.value })
                }
                className="w-full px-2 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm"
              >
                {/* Defensivo: si el cliente ya tenía guardado un prefijo que
                    no está en el catálogo (dato histórico raro), se añade
                    como opción extra para no perderlo silenciosamente al
                    abrir "Editar" sin tocar el campo (mismo patrón que el
                    selector de timezone). */}
                {formData.countryCode &&
                  !orderedCountries.some(
                    (c) => c.code === formData.countryCode,
                  ) && (
                    <option value={formData.countryCode}>
                      {formData.countryCode}
                    </option>
                  )}
                {orderedCountries.map((country) => (
                  <option key={country.iso} value={country.code}>
                    {country.flag} {country.code}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Teléfono *
              </label>
              <input
                type="tel"
                value={formData.phone}
                onChange={(e) =>
                  setFormData({ ...formData, phone: e.target.value })
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                placeholder="600 000 000"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Email
            </label>
            <input
              type="email"
              value={formData.email}
              onChange={(e) =>
                setFormData({ ...formData, email: e.target.value })
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              placeholder="email@ejemplo.com"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Notas
            </label>
            <textarea
              value={formData.notes}
              onChange={(e) =>
                setFormData({ ...formData, notes: e.target.value })
              }
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 resize-none"
              placeholder="Preferencias, alergias, notas especiales..."
            />
          </div>

          <div className="flex flex-wrap items-center gap-4 md:gap-6">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.vipStatus}
                onChange={(e) =>
                  setFormData({ ...formData, vipStatus: e.target.checked })
                }
                className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
              />
              <span className="text-sm text-gray-700">Cliente VIP</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.marketingConsent}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    marketingConsent: e.target.checked,
                  })
                }
                className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
              />
              <span className="text-sm text-gray-700">
                Acepta comunicaciones comerciales
              </span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.acceptsWhatsApp}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    acceptsWhatsApp: e.target.checked,
                  })
                }
                className="w-4 h-4 text-green-600 border-gray-300 rounded focus:ring-green-500"
              />
              <span className="text-sm text-gray-700 flex items-center gap-1">
                <svg
                  className="w-4 h-4 text-green-600"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                >
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                </svg>
                Acepta WhatsApp
              </span>
            </label>
          </div>

          {/* Estado real de comunicaciones (el mismo del filtro/listado), para
              diferenciarlo del consentimiento expreso de la casilla de arriba.
              Solo al editar un cliente existente (uno nuevo aún no tiene estado). */}
          {customer && (
            <div className="flex items-start justify-between gap-3 p-3 bg-gray-50 border border-gray-200 rounded-lg">
              <p className="text-xs text-gray-500 leading-relaxed">
                <span className="font-medium text-gray-600">
                  Estado de comunicaciones.
                </span>{" "}
                La casilla de arriba es el{" "}
                <strong>consentimiento expreso</strong> del cliente (se usa en
                campañas en modo estricto). Este estado indica si sigue de{" "}
                <strong>alta</strong> o se ha dado de <strong>baja</strong> (modo
                suave). No siempre coinciden: un cliente puede estar «Alta» aunque
                no haya marcado el consentimiento.
              </p>
              <EmailStatusBadge status={customer.emailStatus} />
            </div>
          )}

          {/* Aviso cruzado: esta casilla NO reactiva la baja real de campañas
              (gestionada en la ficha 360 vía `marketingEmailOptOut`). Sin
              esto, el hostelero podría pensar que marcarla reincorpora al
              cliente a las campañas cuando en realidad sigue excluido. */}
          {customer?.marketingEmailOptOut && (
            <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg">
              <AlertTriangle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
              <p className="text-xs text-amber-800">
                Este cliente se dio de baja de las campañas de email
                marketing
                {customer.marketingOptOutAt
                  ? ` el ${safeFormatDate(customer.marketingOptOutAt, "d MMM yyyy")}`
                  : ""}
                . Marcar esta casilla <strong>no lo reactiva</strong>: para
                volver a incluirlo en campañas, hazlo desde su ficha de
                cliente (Comunicaciones comerciales).
              </p>
            </div>
          )}

          {/* Preferencias de notificaciones (RGPD opt-out) */}
          <div className="border-t border-gray-200 pt-4">
            <p className="text-sm font-medium text-gray-700 mb-2">
              Preferencias de notificaciones
            </p>
            {customer?.optOutAt && (
              <p className="text-xs text-gray-500 mb-3">
                Baja registrada el{" "}
                {new Date(customer.optOutAt).toLocaleDateString("es-ES")}
                {customer.optOutReason ? ` (${customer.optOutReason})` : ""}
              </p>
            )}
            <div className="space-y-2">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={!formData.transactionalEmailOptOut}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      transactionalEmailOptOut: !e.target.checked,
                    })
                  }
                  className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                />
                <span className="text-sm text-gray-700">
                  Recibir emails de reservas/citas (confirmaciones,
                  recordatorios)
                </span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={!formData.transactionalWhatsAppOptOut}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      transactionalWhatsAppOptOut: !e.target.checked,
                    })
                  }
                  className="w-4 h-4 text-green-600 border-gray-300 rounded focus:ring-green-500"
                />
                <span className="text-sm text-gray-700">
                  Recibir WhatsApps de reservas/citas (confirmaciones,
                  recordatorios)
                </span>
              </label>
            </div>
          </div>
        </div>

        <div className="flex gap-3 p-5 border-t border-gray-200 bg-gray-50 rounded-b-2xl flex-shrink-0">
          <button
            onClick={onClose}
            className="flex-1 py-2.5 bg-white border border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-50 transition-colors"
          >
            Cancelar
          </button>
          <button
            onClick={handleSubmit}
            disabled={saving}
            className="flex-1 py-2.5 bg-primary-600 text-white font-medium rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50"
          >
            {saving ? "Guardando..." : "Guardar"}
          </button>
        </div>
      </div>

      {/* Email Modal */}
      {showEmailModal && customer && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90dvh] overflow-hidden flex flex-col">
            {/* Header */}
            <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center bg-blue-50">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">
                  Enviar Email
                </h3>
                <p className="text-sm text-gray-500">
                  Para: {customer.firstName} {customer.lastName} (
                  {customer.email})
                </p>
              </div>
              <button
                onClick={() => {
                  setShowEmailModal(false);
                  setEmailFormData({ subject: "", message: "" });
                  setEmailAttachments([]);
                  setEmailError(null);
                  setEmailSuccess(null);
                }}
                className="p-2 hover:bg-blue-100 rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            {/* Body */}
            <div className="p-6 overflow-y-auto flex-1 space-y-4">
              {emailError && (
                <div className="p-3 bg-red-50 text-red-700 text-sm rounded-lg flex items-center gap-2">
                  <AlertCircle className="w-4 h-4" />
                  {emailError}
                </div>
              )}
              {emailSuccess && (
                <div className="p-3 bg-green-50 text-green-700 text-sm rounded-lg flex items-center gap-2">
                  <CheckCircle className="w-4 h-4" />
                  {emailSuccess}
                </div>
              )}

              {/* Asunto */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Asunto
                </label>
                <input
                  type="text"
                  value={emailFormData.subject}
                  onChange={(e) =>
                    setEmailFormData({
                      ...emailFormData,
                      subject: e.target.value,
                    })
                  }
                  placeholder="Asunto del email..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              {/* Mensaje */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Mensaje
                </label>
                <textarea
                  value={emailFormData.message}
                  onChange={(e) =>
                    setEmailFormData({
                      ...emailFormData,
                      message: e.target.value,
                    })
                  }
                  placeholder="Escribe tu mensaje..."
                  rows={8}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                />
              </div>

              {/* Adjuntos */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Adjuntos
                </label>
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center hover:border-blue-400 transition-colors">
                  <input
                    type="file"
                    multiple
                    onChange={(e) => {
                      if (e.target.files) {
                        setEmailAttachments(Array.from(e.target.files));
                      }
                    }}
                    className="hidden"
                    id="customer-email-attachments"
                  />
                  <label
                    htmlFor="customer-email-attachments"
                    className="cursor-pointer flex flex-col items-center gap-2"
                  >
                    <Paperclip className="w-6 h-6 text-gray-400" />
                    <span className="text-sm text-gray-500">
                      Haz clic para adjuntar archivos
                    </span>
                  </label>
                </div>
                {emailAttachments.length > 0 && (
                  <div className="mt-2 space-y-1">
                    {emailAttachments.map((file, index) => (
                      <div
                        key={index}
                        className="flex items-center justify-between bg-gray-50 px-3 py-2 rounded-lg text-sm"
                      >
                        <span className="truncate">{file.name}</span>
                        <button
                          onClick={() => {
                            setEmailAttachments(
                              emailAttachments.filter((_, i) => i !== index),
                            );
                          }}
                          className="text-red-500 hover:text-red-700 ml-2"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Footer */}
            <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3 bg-gray-50">
              <button
                onClick={() => {
                  setShowEmailModal(false);
                  setEmailFormData({ subject: "", message: "" });
                  setEmailAttachments([]);
                  setEmailError(null);
                  setEmailSuccess(null);
                }}
                className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors text-sm font-medium"
              >
                Cancelar
              </button>
              <button
                onClick={handleSendEmail}
                disabled={
                  emailSending ||
                  !emailFormData.subject.trim() ||
                  !emailFormData.message.trim()
                }
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {emailSending ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Enviando...
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4" />
                    Enviar Email
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Import Modal Component
